import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import { ChevronLeft, ChevronRight, X, ArrowRight, Maximize2, Minimize2 } from 'lucide-react';
import CustomCursor from '../components/CustomCursor';
import CodeRain from '../components/CodeRain';

/* ──────────────────────────────────────────────────────────────
   CONFIG — replace artwork by editing this array only.
   All five card fronts are static 2D images (WebP), same aspect.
   ────────────────────────────────────────────────────────────── */
const CARDS = [
  { id: 1, src: '/thisishumanitycards/card1.webp', alt: 'Mr. Relative — collectible art card, edition design 01' },
  { id: 2, src: '/thisishumanitycards/card2.webp', alt: 'Mr. Relative — collectible art card, edition design 02' },
  { id: 3, src: '/thisishumanitycards/card3.webp', alt: 'Mr. Relative — collectible art card, edition design 03' },
  { id: 4, src: '/thisishumanitycards/card4.webp', alt: 'Mr. Relative — collectible art card, edition design 04' },
  { id: 5, src: '/thisishumanitycards/card5.webp', alt: 'Mr. Relative — collectible art card, edition design 05' },
];
const N = CARDS.length;
const CY = '#90E0EF';

/* Reading-time based hero messaging — slowed for comfortable reading.
   Brand lines show everywhere; step lines are added on the kiosk so an
   unattended tablet explains the whole flow on top of the artwork. */
const BRAND_MESSAGES = [
  { lines: ['LIMITED EDITION', 'COLLECTIBLE ART CARDS'], sub: '150 EDITIONS', hold: 6500 },
  { lines: ['HAND-SIGNED', '& UNIQUELY NUMBERED'], sub: null, hold: 6000 },
  { lines: ['3 FRAMED ARTWORKS', 'TO WIN'], drawSub: true, hold: 6800 },
];
const STEP_MESSAGES = [
  { num: '01', lines: ['Pick your card'], body: 'Choose any available card from the collection.', hold: 6500 },
  { num: '02', lines: ['Register your edition'], body: 'Purchase the card and register its unique edition number.', hold: 6800 },
  { num: '03', lines: ['Keep the card'], body: 'Every card is hand-signed, individually numbered, and yours to keep as a collectible artwork.', hold: 7800 },
  { num: '04', lines: ['Enter the draw'], body: 'Every purchased edition is automatically entered into the final draw.', hold: 6800 },
  { lines: ['3 editions', 'will be drawn'], body: 'Each winning edition receives one framed artwork by Mr. Relative.', drawSub: true, hold: 7800 },
];

/* Per-card idle float signatures (px, pre-scale — depth emerges from slot scale). */
const FLOAT = [
  { fx: 26, fy: 20, rot: 2.2, dur: 11 },
  { fx: 20, fy: 26, rot: -2.6, dur: 13 },
  { fx: 30, fy: 16, rot: 2.8, dur: 12 },
  { fx: 22, fy: 22, rot: -2.0, dur: 14 },
  { fx: 18, fy: 24, rot: 2.4, dur: 10 },
];

/* Art-directed composition slots (x/y as % of container from centre). */
const SLOTS_DESKTOP = [
  { x: 14, y: -2, s: 1.0, z: 60, o: 1.0 },
  { x: -24, y: -14, s: 0.8, z: 48, o: 0.95 },
  { x: 34, y: 16, s: 0.7, z: 40, o: 0.92 },
  { x: -8, y: 22, s: 0.62, z: 30, o: 0.85 },
  { x: 28, y: -24, s: 0.52, z: 18, o: 0.75 },
];
const SLOTS_MOBILE = [
  { x: 0, y: -4, s: 0.96, z: 60, o: 1.0 },
  { x: -30, y: -28, s: 0.52, z: 40, o: 0.62 },
  { x: 32, y: -16, s: 0.5, z: 38, o: 0.6 },
  { x: 28, y: 26, s: 0.5, z: 30, o: 0.55 },
  { x: -30, y: 24, s: 0.46, z: 28, o: 0.5 },
];

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/* ──────────────────────────────────────────────────────────────
   HERO CARD — one reusable component, pure 2D transforms.
   ────────────────────────────────────────────────────────────── */
function HeroCard({ card, index, slotPx, slot, baseH, reduced, pointerX, pointerY, focusedIndex, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const isFocusedAny = focusedIndex !== null;
  const isThis = focusedIndex === index;

  const depth = [12, 8, 6, 5, 4][index % 5];
  const px = useTransform(pointerX, [-1, 1], [-depth, depth]);
  const py = useTransform(pointerY, [-1, 1], [-depth * 0.7, depth * 0.7]);
  const { fx, fy, rot, dur } = FLOAT[index % 5];

  const outerOpacity = isThis ? 0 : isFocusedAny ? 0.12 : slot.o;
  const outerScale = isFocusedAny && !isThis ? slot.s * 0.9 : slot.s;

  return (
    <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
      <motion.div
        className="relative"
        data-testid={`hero-card-${index}`}
        style={{ zIndex: hovered ? 200 : slot.z, pointerEvents: isFocusedAny ? 'none' : 'auto', cursor: 'pointer' }}
        animate={{ x: slotPx.x, y: slotPx.y, scale: outerScale, opacity: outerOpacity }}
        transition={{ duration: reduced ? 0.6 : 3.2, ease: [0.4, 0, 0.2, 1] }}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        onClick={() => onSelect(index)}
      >
        <motion.div
          animate={{ scale: hovered ? 1.06 : 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{ filter: hovered ? 'drop-shadow(0 30px 60px rgba(0,0,0,0.55))' : 'drop-shadow(0 16px 40px rgba(0,0,0,0.4))' }}
        >
          <motion.div style={{ x: px, y: py }}>
            <motion.div
              animate={reduced ? {} : { x: [0, fx, 0, -fx, 0], y: [0, -fy, 0, fy, 0], rotate: [0, rot, 0, -rot, 0] }}
              transition={reduced ? {} : { duration: dur, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img
                src={card.src}
                alt={card.alt}
                draggable={false}
                fetchPriority="high"
                className="select-none block"
                style={{ height: baseH, width: 'auto', userSelect: 'none' }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   FOCUS VIEWER — shared by hero + collection. Flat 2D, no 3D.
   ────────────────────────────────────────────────────────────── */
function FocusViewer({ index, onClose, onPrev, onNext, isMobile }) {
  const startX = useRef(null);
  if (index === null) return null;
  const card = CARDS[index];

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 60) onPrev();
    else if (dx < -60) onNext();
    startX.current = null;
  };

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'rgba(3,3,3,0.94)', backdropFilter: 'blur(4px)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      onClick={onClose}
      data-testid="focus-viewer"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="focus-close"
        className="absolute top-6 right-6 text-[#888] hover:text-white transition-colors"
        style={{ cursor: 'pointer' }}
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {!isMobile && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="focus-prev"
            className="absolute left-6 md:left-10 text-[#777] hover:text-white transition-colors"
            style={{ cursor: 'pointer' }}
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="focus-next"
            className="absolute right-6 md:right-10 text-[#777] hover:text-white transition-colors"
            style={{ cursor: 'pointer' }}
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={card.id}
          className="flex flex-col items-center"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          initial={{ opacity: 0, scale: 0.94, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: -6 }}
          transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
        >
          <img
            src={card.src}
            alt={card.alt}
            draggable={false}
            className="select-none block"
            style={{ height: isMobile ? '70svh' : '76vh', width: 'auto', filter: 'drop-shadow(0 40px 90px rgba(0,0,0,0.6))' }}
          />
          <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.28em] text-[#777]">
            Mr. Relative &nbsp;·&nbsp; Design {String(index + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}
          </p>
          {isMobile && (
            <div className="mt-5 flex items-center gap-10">
              <button onClick={onPrev} data-testid="focus-prev-m" style={{ cursor: 'pointer' }} className="text-[#888] hover:text-white"><ChevronLeft className="w-7 h-7" /></button>
              <button onClick={onNext} data-testid="focus-next-m" style={{ cursor: 'pointer' }} className="text-[#888] hover:text-white"><ChevronRight className="w-7 h-7" /></button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

/* Animated count-up ticker — tweens + pulses when the value changes. */
function AnimatedCounter({ value }) {
  const [display, setDisplay] = useState(value ?? 0);
  const [bump, setBump] = useState(false);
  const prev = useRef(value ?? 0);
  useEffect(() => {
    if (value == null) return undefined;
    const from = prev.current;
    const to = value;
    if (from === to) { setDisplay(to); return undefined; }
    setBump(true);
    const dur = 900;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else { prev.current = to; setTimeout(() => setBump(false), 260); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <motion.span
      className="inline-block"
      style={{ fontVariantNumeric: 'tabular-nums' }}
      animate={{ scale: bump ? 1.22 : 1, color: bump ? CY : '#ffffff' }}
      transition={{ duration: 0.28, ease: 'easeOut' }}
    >
      {display}
    </motion.span>
  );
}

/* Scroll reveal wrapper. */
function Reveal({ children, className = '', delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────────
   PAGE
   ────────────────────────────────────────────────────────────── */
export default function ThisIsHumanityCardsPage() {
  const heroRef = useRef(null);
  const [size, setSize] = useState({ w: 1280, h: 800 });
  const [rot, setRot] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [reduced, setReduced] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const [stats, setStats] = useState(null);
  const [drawDisplay, setDrawDisplay] = useState('27.09.2026');

  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const lastInteraction = useRef(Date.now());
  const focusRef = useRef(null);
  focusRef.current = focusedIndex;

  const isMobile = size.w < 768;
  // Kiosk = single no-scroll screen (Galaxy Tab landscape at the fair).
  // Auto-detected (coarse pointer + touch + landscape), or forced with
  // ?kiosk=1 / disabled with ?kiosk=0 (handy for previewing on desktop).
  const isKiosk = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const q = new URLSearchParams(window.location.search).get('kiosk');
    if (q === '1' || q === 'true') return true;
    if (q === '0' || q === 'false') return false;
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const landscape = size.w > size.h && size.w >= 900;
    return !!(coarse && touch && landscape);
  }, [size]);

  // Kiosk shows the full step-by-step rotation; the normal site keeps the
  // brand lines in the hero (the steps live in its scrolling section).
  const messages = useMemo(
    () => (isKiosk ? [...BRAND_MESSAGES, ...STEP_MESSAGES] : BRAND_MESSAGES),
    [isKiosk],
  );
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  /* lock page scroll in kiosk mode — one screen, no scrolling */
  useEffect(() => {
    if (!isKiosk) return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevBody; document.documentElement.style.overflow = prevHtml; };
  }, [isKiosk]);

  /* reduced-motion + preload */
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    const links = CARDS.map((c) => {
      const l = document.createElement('link');
      l.rel = 'preload'; l.as = 'image'; l.href = c.src;
      document.head.appendChild(l);
      return l;
    });
    return () => { mq.removeEventListener?.('change', onChange); links.forEach((l) => l.remove()); };
  }, []);

  /* size tracking — window dims are reliable regardless of layout timing */
  useEffect(() => {
    const measure = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  const markInteract = useCallback(() => { lastInteraction.current = Date.now(); }, []);

  /* live editions counter (polls) */
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let stop = false;
    const load = async () => {
      try { const { data } = await axios.get(`${API}/public/cards/stats`); if (!stop) setStats(data); } catch { /* keep last */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  /* card config — admin-editable draw date */
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let stop = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/cards/config`);
        if (!stop && data?.draw_date_display) setDrawDisplay(data.draw_date_display);
      } catch { /* keep default */ }
    })();
    return () => { stop = true; };
  }, []);

  /* fullscreen */
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.();
  }, []);

  /* interaction listeners (pointer / touch / scroll / key) */
  useEffect(() => {
    const onScroll = () => markInteract();
    const onKey = (e) => {
      markInteract();
      if (focusRef.current === null) return;
      if (e.key === 'Escape') setFocusedIndex(null);
      else if (e.key === 'ArrowLeft') setFocusedIndex((i) => (i + N - 1) % N);
      else if (e.key === 'ArrowRight') setFocusedIndex((i) => (i + 1) % N);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('keydown', onKey); };
  }, [markInteract]);

  /* autonomous recomposition — paused while interacting, resumes when idle */
  useEffect(() => {
    if (reduced) return undefined;
    let id;
    const tick = () => {
      const idleFor = Date.now() - lastInteraction.current;
      if (focusRef.current === null && idleFor > 6000) setRot((r) => r + 1);
      id = setTimeout(tick, 7000 + Math.random() * 5000);
    };
    id = setTimeout(tick, 7000 + Math.random() * 5000);
    return () => clearTimeout(id);
  }, [reduced]);

  /* headline rotation (reading-time based) — paused while a card is focused.
     Reads the message list from a ref so unrelated re-renders never reset
     the timer; only msgIndex / focus / kiosk-mode drive it. */
  useEffect(() => {
    if (focusedIndex !== null) return undefined;
    const list = messagesRef.current;
    const safeIndex = msgIndex % list.length;
    const id = setTimeout(
      () => setMsgIndex((i) => (i + 1) % messagesRef.current.length),
      list[safeIndex].hold,
    );
    return () => clearTimeout(id);
  }, [msgIndex, focusedIndex, isKiosk]);

  /* tablet / kiosk idle return: if a visitor left a card open, close it and
     restart the attract loop. When nothing is focused, let the rotation keep
     cycling through every message on its own (don't cut it short). */
  useEffect(() => {
    if (!isKiosk) return undefined;
    const id = setInterval(() => {
      if (focusRef.current !== null && Date.now() - lastInteraction.current > 30000) {
        setFocusedIndex(null);
        setMsgIndex(0);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [isKiosk]);

  const onPointerMove = (e) => {
    markInteract();
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches && heroRef.current) {
      const r = heroRef.current.getBoundingClientRect();
      pointerX.set(clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1));
      pointerY.set(clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1));
    }
  };

  /* slots for current viewport, with adaptive horizontal spread */
  const slots = isMobile ? SLOTS_MOBILE : SLOTS_DESKTOP;
  const xSpread = isMobile ? 1 : clamp(size.w / 1200, 0.85, 1.3);
  // base card height in px (a little smaller than before → more breathing room)
  const baseHpx = isMobile ? clamp(0.42 * size.h, 210, 380) : clamp(0.42 * size.h, 240, 460);
  const CARD_RATIO = 1064 / 1500; // width / height

  const openFocus = (i) => { markInteract(); setFocusedIndex(i); };
  const message = messages[msgIndex % messages.length];

  return (
    <div className={`bg-[#050505] text-[#f0f0f0] ${isKiosk ? 'h-[100svh] overflow-hidden' : 'min-h-[100svh] overflow-x-hidden'}`} data-testid="thisishumanitycards-page">
      <CustomCursor />
      <CodeRain />

      {/* Fixed top controls — hidden while a card is focused */}
      {focusedIndex === null && (
        <>
          {/* always-visible live editions counter (centered) */}
          <div
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[130]"
            style={{ pointerEvents: 'none' }}
            data-testid="editions-counter"
          >
            <div className="flex items-center gap-3 px-5 py-2.5 border border-[#ffffff1a]" style={{ background: 'rgba(5,5,5,0.72)', backdropFilter: 'blur(10px)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: CY, boxShadow: `0 0 10px ${CY}` }} />
              <span className="font-outfit font-bold text-white leading-none" style={{ fontSize: 'clamp(1.15rem, 3.4vw, 1.9rem)' }}>
                {stats ? <AnimatedCounter value={stats.claimed} /> : '—'}
                <span className="text-[#4a4a4a] mx-1.5">/</span>
                {stats ? stats.total : 150}
              </span>
              <span className="font-mono uppercase tracking-[0.24em] text-[10px] md:text-xs text-[#9a9a9a]">claimed</span>
            </div>
          </div>

          {/* fullscreen toggle */}
          <button
            onClick={toggleFullscreen}
            data-testid="fullscreen-btn"
            aria-label={isFs ? 'Exit fullscreen' : 'Enter fullscreen'}
            className="fixed top-6 right-6 md:top-7 md:right-8 z-[130] text-[#888] hover:text-white transition-colors"
            style={{ cursor: 'pointer' }}
          >
            {isFs ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </>
      )}

      <div className="relative z-10">
      {/* ══ 01 · HERO — interactive 2D card installation ══ */}
      <section
        ref={heroRef}
        onPointerMove={onPointerMove}
        onPointerDown={markInteract}
        onTouchStart={markInteract}
        className="relative w-full overflow-hidden"
        style={{ height: '100svh' }}
        data-testid="hero-section"
      >
        {/* top brand micro-label */}
        <div className="absolute top-6 left-6 md:top-8 md:left-10 z-[80] flex items-center gap-2.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#cfcfcf]">Mr. Relative</span>
          <span className="hidden 2xl:inline text-[10px] font-mono uppercase tracking-[0.3em] text-[#555]">// This is Humanity</span>
        </div>

        {/* cards */}
        {CARDS.map((card, i) => {
          const slot = slots[(i + rot) % N];
          // keep every card fully inside the viewport, accounting for its
          // scaled size + idle-float/proximity travel (pad).
          const cardH = baseHpx * slot.s;
          const cardW = cardH * CARD_RATIO;
          const pad = 56;
          const maxX = Math.max(0, size.w / 2 - cardW / 2 - pad);
          const maxY = Math.max(0, size.h / 2 - cardH / 2 - pad);
          const rawX = (slot.x / 100) * size.w * xSpread;
          const rawY = (slot.y / 100) * size.h;
          const slotPx = { x: clamp(rawX, -maxX, maxX), y: clamp(rawY, -maxY, maxY) };
          const baseH = `${Math.round(baseHpx)}px`;
          return (
            <HeroCard
              key={card.id}
              card={card}
              index={i}
              slot={slot}
              slotPx={slotPx}
              baseH={baseH}
              reduced={reduced}
              pointerX={pointerX}
              pointerY={pointerY}
              focusedIndex={focusedIndex}
              onSelect={openFocus}
            />
          );
        })}

        {/* bottom scrim for legibility */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[42%] z-[90]" style={{ background: 'linear-gradient(to top, rgba(5,5,5,0.92) 8%, rgba(5,5,5,0.55) 45%, transparent 100%)' }} />

        {/* rotating headline + permanent line */}
        <div className="absolute bottom-0 inset-x-0 z-[95] px-6 md:px-10 pb-8 md:pb-12">
          <div className="relative" style={{ minHeight: isMobile ? '170px' : isKiosk ? '280px' : '200px' }}>
            <AnimatePresence>
              <motion.div
                key={msgIndex}
                className="absolute bottom-0 left-0 right-0 text-center md:text-left"
                initial={{ opacity: 0, y: reduced ? 0 : 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduced ? 0 : -8 }}
                transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
                data-testid="hero-headline"
              >
                {message.num && (
                  <p
                    className="font-outfit font-bold mb-3 md:mb-4"
                    style={{ fontSize: 'clamp(1.5rem, 3.6vw, 2.6rem)', lineHeight: 1, color: CY }}
                  >
                    {message.num}
                  </p>
                )}
                {message.lines.map((line) => (
                  <h1
                    key={line}
                    className="font-outfit font-bold text-white tracking-tight leading-[0.98]"
                    style={{ fontSize: message.body ? 'clamp(1.9rem, 5vw, 3.6rem)' : 'clamp(2rem, 6.2vw, 5rem)' }}
                  >
                    {line}
                  </h1>
                ))}
                {message.body && (
                  <p
                    className="mt-4 text-[#cfcfcf] font-light leading-relaxed max-w-2xl mx-auto md:mx-0"
                    style={{ fontSize: 'clamp(0.95rem, 1.9vw, 1.3rem)' }}
                  >
                    {message.body}
                  </p>
                )}
                {(message.sub || message.drawSub) && (
                  <p className="mt-3 font-mono uppercase tracking-[0.24em] text-sm md:text-base" style={{ color: CY }}>
                    {message.drawSub ? `DRAW — ${drawDisplay}` : message.sub}
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-6 pt-4 border-t border-[#1c1c1c] text-center md:text-left">
            <p className="text-[10px] md:text-[11px] font-mono uppercase tracking-[0.22em] text-[#8a8a8a]" data-testid="hero-permanent-line">
              150 EDITIONS <span className="text-[#3a3a3a]">//</span> £20 / CARD <span className="text-[#3a3a3a]">//</span> DRAW {drawDisplay}
            </p>
          </div>
        </div>
      </section>

      {!isKiosk && (
      <>
      {/* ══ 02 · INTRO ══ */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-36 max-w-5xl mx-auto" data-testid="intro-section">
        <Reveal>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#666] mb-8">The Edition</p>
          <h2 className="font-outfit font-bold text-white tracking-tight leading-[1.02]" style={{ fontSize: 'clamp(1.9rem, 5vw, 3.4rem)' }}>
            150 hand-signed<br />art cards
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-8 md:gap-14">
          <Reveal delay={0.05}>
            <p className="text-base md:text-lg text-[#cfcfcf] leading-relaxed font-light">
              Each card is a physical collectible artwork, individually hand-signed and uniquely numbered.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-base md:text-lg text-[#cfcfcf] leading-relaxed font-light">
              Purchase a card and its edition number is automatically entered into the final draw. Three winning editions will each receive a framed artwork by Mr. Relative.
            </p>
            <p className="mt-6 font-mono uppercase tracking-[0.24em] text-sm" style={{ color: CY }}>Draw — {drawDisplay}</p>
            <p className="mt-2 text-xs font-mono text-[#777] leading-relaxed">
              Winners will be announced publicly and contacted directly by email.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══ 03 · HOW IT WORKS ══ */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-28 border-t border-[#141414]" data-testid="how-section">
        <div className="max-w-6xl mx-auto">
          <Reveal><h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#888] mb-14">How it works</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
            {[
              ['01', 'Pick your card', 'Choose any available card from the collection.'],
              ['02', 'Register your edition', 'Purchase the card and register its unique edition number.'],
              ['03', 'Keep the card', 'Every card is hand-signed, individually numbered, and yours to keep as a collectible artwork.'],
              ['04', 'Enter the draw', 'Every purchased edition is automatically entered into the final draw.'],
            ].map(([num, title, body], i) => (
              <Reveal key={num} delay={i * 0.08}>
                <div className="border-t border-[#242424] pt-5">
                  <span className="font-outfit font-bold text-[#2f2f2f]" style={{ fontSize: '2.4rem', lineHeight: 1 }}>{num}</span>
                  <h3 className="mt-4 font-outfit font-semibold text-white text-lg">{title}</h3>
                  <p className="mt-3 text-sm text-[#9a9a9a] leading-relaxed font-light">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-20 md:mt-28 text-center">
              <h3 className="font-outfit font-bold text-white tracking-tight" style={{ fontSize: 'clamp(1.6rem, 4.5vw, 2.8rem)' }}>3 editions will be drawn</h3>
              <p className="mt-5 text-sm md:text-base text-[#aaa] font-light max-w-xl mx-auto leading-relaxed">
                Each winning edition receives one framed artwork by Mr. Relative.
              </p>
              <p className="mt-5 font-mono uppercase tracking-[0.24em] text-sm" style={{ color: CY }}>Draw — {drawDisplay}</p>
              <p className="mt-2 text-xs font-mono text-[#777]">Winning edition numbers announced publicly · winners emailed directly.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ 04 · THE COLLECTION ══ */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-28 border-t border-[#141414]" data-testid="collection-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#666] mb-4">The Collection</p>
                <h2 className="font-outfit font-bold text-white tracking-tight leading-[1.02]" style={{ fontSize: 'clamp(1.8rem, 4.6vw, 3rem)' }}>
                  150 editions.<br />one collection.
                </h2>
              </div>
              <div className="md:text-right">
                <p className="text-sm text-[#a8a8a8] font-light max-w-sm leading-relaxed">
                  Each card is a physical, hand-signed collectible artwork with its own unique edition number. Once an edition is sold, it is gone.
                </p>
                <p className="mt-5 font-mono uppercase tracking-[0.24em] text-sm" style={{ color: CY }}>£20 / card</p>
              </div>
            </div>
          </Reveal>

          {/* editorial arrangement: featured + supporting */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {CARDS.map((card, i) => (
              <Reveal
                key={card.id}
                delay={i * 0.05}
                className={i === 0 ? 'col-span-2 row-span-2 lg:col-span-2 lg:row-span-2' : 'col-span-1'}
              >
                <button
                  onClick={() => openFocus(i)}
                  data-testid={`collection-card-${i}`}
                  className="group relative w-full overflow-hidden bg-[#0a0a0a] block"
                  style={{ cursor: 'pointer', aspectRatio: '1064 / 1500' }}
                >
                  <img
                    src={card.src}
                    alt={card.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                  />
                  <span className="absolute bottom-3 left-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">
                    Design {String(i + 1).padStart(2, '0')}
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 05 · THE DRAW ══ */}
      <section className="px-6 md:px-10 lg:px-16 py-32 md:py-48 border-t border-[#141414]" data-testid="draw-section">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#666] mb-10">The Draw</p>
            <h2 className="font-outfit font-bold text-white tracking-tight leading-[0.98]" style={{ fontSize: 'clamp(2.4rem, 8vw, 6rem)' }}>
              3 framed<br />artworks<br /><span style={{ color: CY }}>to win</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-14 text-base md:text-lg text-[#bdbdbd] font-light max-w-xl mx-auto leading-relaxed">
              Every purchased card enters its unique edition number into the draw.
            </p>
            <div className="mt-14 flex flex-col items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#666]">Draw</span>
              <span className="font-outfit font-bold text-white tracking-tight" style={{ fontSize: 'clamp(1.8rem, 5vw, 3rem)' }}>{drawDisplay}</span>
            </div>
            <p className="mt-16 font-mono uppercase tracking-[0.26em] text-xs md:text-sm text-[#9a9a9a] leading-loose">
              Winners will be<br className="sm:hidden" /> announced publicly<br />
              <span className="text-[#3a3a3a]">+</span> contacted by email
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══ 06 · FINAL CTA ══ */}
      <section className="px-6 md:px-10 lg:px-16 py-28 md:py-40 border-t border-[#141414]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-outfit font-bold text-white tracking-tight leading-[1.0]" style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}>
              Collect an edition
            </h2>
            <p className="mt-6 font-mono uppercase tracking-[0.26em] text-sm md:text-base" style={{ color: CY }}>£20 / card</p>
            {stats && (
              <div className="mt-8 inline-flex items-center gap-3" data-testid="cta-editions-counter">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
                <span className="font-mono uppercase tracking-[0.24em] text-xs md:text-sm text-[#cfcfcf]">
                  <span className="text-white font-medium"><AnimatedCounter value={stats.claimed} /></span> / {stats.total} editions claimed
                </span>
              </div>
            )}
            <div className="mt-12">
              <Link
                to="/cards/buy"
                data-testid="buy-a-card-cta"
                className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-[#050505] text-[11px] font-mono uppercase tracking-[0.24em] font-medium transition-colors hover:bg-[#90E0EF]"
                style={{ cursor: 'pointer' }}
              >
                Buy a card
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <p className="mt-8 text-xs font-mono text-[#666] leading-relaxed max-w-md mx-auto">
              150 editions · hand-signed · uniquely numbered · every purchase enters the draw for 3 framed artworks.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="px-6 md:px-10 lg:px-16 py-14 border-t border-[#141414]" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] font-mono uppercase tracking-[0.24em] text-[#8a8a8a]">
            <span className="text-white">Mr. Relative</span>
            <span className="text-[#3a3a3a]">/</span>
            <span>This is Humanity</span>
            <span className="text-[#3a3a3a]">/</span>
            <span>The Relative Gallery</span>
          </div>
          <div className="flex items-center gap-6 text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">
            <Link to="/" className="hover:text-white transition-colors">Gallery</Link>
            <Link to="/cards/buy" className="hover:text-white transition-colors">Buy a card</Link>
            <span>2026</span>
          </div>
        </div>
      </footer>
      </>
      )}

      {/* Focus viewer (shared) */}
      <AnimatePresence>
        {focusedIndex !== null && (
          <FocusViewer
            index={focusedIndex}
            isMobile={isMobile}
            onClose={() => setFocusedIndex(null)}
            onPrev={() => setFocusedIndex((i) => (i + N - 1) % N)}
            onNext={() => setFocusedIndex((i) => (i + 1) % N)}
          />
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}
